import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../assets/images/Logo.svg';
import { useAuth } from '../../contexts/AuthContext';
import { listNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../../services/notifications';

const Header = () => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotif, setLoadingNotif] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const loadNotifications = async () => {
    try {
      setLoadingNotif(true);
      const res = await listNotifications({ page: 1, limit: 50 });
      if (res.success) {
        setNotifications(res.data?.notifications || []);
        setUnreadCount(res.data?.unreadCount || 0);
      }
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoadingNotif(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // Only reload notifications when dropdown is opened, not continuously
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      await loadNotifications();
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read first
    if (!notification.read) {
      await handleMarkRead(notification._id || notification.id);
    }
    
    // Close dropdown
    setIsNotifOpen(false);
    
    // Navigate based on notification type and meta data
    const meta = notification.meta || {};
    
    if (notification.type === 'translation_requested' || 
        notification.type === 'translation_approved' || 
        notification.type === 'translation_completed' ||
               notification.type === 'translation_status_changed' ||
               notification.type === 'translation_request_status' ||
               notification.type === 'translation_files_uploaded') {
      // For translation requests, navigate to license details with translations tab
      if (meta.licenceId) {
        navigate(`/licence-details/${meta.licenceId}?tab=translations`);
      } else if (meta.translationRequestId) {
        // If we have translationRequestId but no licenceId, we need to fetch it
        // For now, navigate to requested licenses page
        navigate('/requested-licences');
      }
    } else if (notification.type === 'license_requested' || 
               notification.type === 'license_approved' || 
               notification.type === 'license_rejected' || 
               notification.type === 'license_updated' ||
               notification.type === 'license_ready_for_approval' ||
               notification.type === 'license_sent_back' ||
               notification.type === 'license_expiration_set' ||
               notification.type === 'license_expiring_soon' ||
               notification.type === 'license_expiring_tomorrow' ||
               notification.type === 'license_expired' ||
               notification.type === 'license_archived' ||
               notification.type === 'license_status_changed' ||
               notification.type === 'license_under_review' ||
               notification.type === 'license_awaiting_review' ||
               notification.type === 'payment_status_changed') {
      // For license requests, navigate to license details
      if (meta.licenceId) {
        navigate(`/licence-details/${meta.licenceId}`);
      }
    } else if (notification.type === 'message_received' || notification.type === 'message_sent') {
      if (meta.licenceId) {
        navigate(`/licence-details/${meta.licenceId}`);
      } else {
        navigate('/messages');
      }
    } else if (
      notification.type === 'support_ticket_received' &&
      meta.supportTicketId
    ) {
      navigate(`/messages?supportTicketId=${meta.supportTicketId}`);
    } else if (notification.type === 'invoice_generated' || 
               notification.type === 'invoice_sent' || 
               notification.type === 'invoice_paid' ||
	               notification.type === 'invoice_voided' ||
	               notification.type === 'invoice_refunded' ||
	               notification.type === 'payment_completed' ||
	               notification.type === 'payment_failed' ||
	               notification.type === 'payment_pending') {
      // For invoices, navigate to invoice details if available
      if (meta.invoiceId) {
        navigate(`/invoices/${meta.invoiceId}`);
      } else if (meta.licenceId) {
        navigate(`/licence-details/${meta.licenceId}`);
      }
    } else if (notification.type === 'quotation_submitted' ||
               notification.type === 'quotation_under_review' ||
               notification.type === 'quotation_reviewed' ||
               notification.type === 'quotation_signed' ||
               notification.type === 'quotation_approved' ||
               notification.type === 'quotation_rejected' ||
               notification.type === 'quotation_archived' ||
               notification.type === 'quotation_ready_for_approval' ||
               notification.type === 'quotation_sent_back') {
      if (meta.quotationId) {
        navigate(`/quotations/${meta.quotationId}`);
      } else if (meta.licenceId) {
        navigate(`/licence-details/${meta.licenceId}`);
      }
    } else if (meta.actionUrl) {
      // If there's an actionUrl in meta, use it
      window.location.href = meta.actionUrl;
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsAsRead();
      await loadNotifications();
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}hrs`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const groupNotifications = (list) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const grouped = { today: [], yesterday: [], older: [] };
    list.forEach((n) => {
      const d = new Date(n.createdAt);
      if (d >= today) grouped.today.push(n);
      else if (d >= yesterday) grouped.yesterday.push(n);
      else grouped.older.push(n);
    });
    return grouped;
  };

  const getIcon = (type) => {
    const base = 'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0';
    switch (type) {
      case 'license_approved':
      case 'translation_completed':
      case 'payment_completed':
        return (
          <div className={`${base} bg-[#EBF9F3]`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#40C191" strokeWidth="1.5">
              <path d="M16.6667 5L7.5 14.1667L3.33333 10" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'payment_failed':
      case 'license_rejected':
        return (
          <div className={`${base} bg-[#FEF2F2]`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#F87171" strokeWidth="1.5">
              <path d="M10 6.66667V10" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 13.3333H10.0083" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 16.6667C13.6819 16.6667 16.6667 13.6819 16.6667 10C16.6667 6.31811 13.6819 3.33334 10 3.33334C6.3181 3.33334 3.33334 6.31811 3.33334 10C3.33334 13.6819 6.3181 16.6667 10 16.6667Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'payment_pending':
      case 'license_updated':
        return (
          <div className={`${base} bg-[#FFF9E9]`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#FFB547" strokeWidth="1.5">
              <path d="M10 7.5V10.8333" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 14.1667H10.0083" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.95 3.54167L1.68333 15.8333C1.55 16.0733 1.48727 16.3489 1.50223 16.6258C1.5172 16.9028 1.60923 17.1689 1.76733 17.3927C1.92543 17.6165 2.14271 17.7889 2.39462 17.8894C2.64654 17.99 2.92148 18.0146 3.18333 17.9583H16.8167C17.0785 18.0146 17.3534 17.99 17.6054 17.8894C17.8573 17.7889 18.0746 17.6165 18.2327 17.3927C18.3908 17.1689 18.4828 16.9028 18.4978 16.6258C18.5127 16.3489 18.45 16.0733 18.3167 15.8333L11.05 3.54167C10.9012 3.31869 10.6868 3.13963 10.4336 3.02433C10.1803 2.90902 9.89766 2.86157 9.61666 2.88733C9.33565 2.91309 9.06908 3.01097 8.84747 3.17134C8.62585 3.33171 8.45796 3.54821 8.36666 3.79167L8.95 3.54167Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      default:
        return (
          <div className={`${base} bg-[#F1F5F9]`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#64748B" strokeWidth="1.5">
              <path d="M10 9.16667V13.3333" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 6.66667H10.0083" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 16.6667C13.6819 16.6667 16.6667 13.6819 16.6667 10C16.6667 6.31811 13.6819 3.33334 10 3.33334C6.3181 3.33334 3.33334 6.31811 3.33334 10C3.33334 13.6819 6.3181 16.6667 10 16.6667Z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
    }
  };

  const groupedNotifs = groupNotifications(notifications);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Left Side - Logo */}
        <Link to="/" className="flex items-center">
          <img 
            src={Logo} 
            alt="ProFound - United for Better Outcomes" 
            className="h-10"
            onError={(e) => {
              // Fallback to text logo if image fails to load
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `
                <div class="flex items-center gap-2">
                  <div class="flex items-baseline">
                    <span class="text-2xl font-bold text-gray-900">PRO</span>
                    <span class="text-2xl font-bold text-[#7CC142]">found</span>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-[10px] text-gray-600 uppercase tracking-wider leading-tight">United for</span>
                    <span class="text-[10px] text-gray-600 uppercase tracking-wider leading-tight">Better Outcomes</span>
                  </div>
                </div>
              `;
            }}
          />
        </Link>

        {/* Right Side */}
        <div className="flex items-center space-x-4">
          {/* User Role Badge */}
          {user?.roles && user.roles.length > 0 && (
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <span className="text-xs font-medium text-emerald-700">
                {user.roles[0]}
              </span>
            </div>
          )}
          
          {/* Notifications */}
          <div className="relative">
            <button
              className="relative p-2 text-gray-600 hover:text-emerald-400 transition-colors rounded-lg hover:bg-gray-100"
              onClick={() => setIsNotifOpen((o) => !o)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M15 6.66667C15 5.34058 14.4732 4.06881 13.5355 3.13113C12.5979 2.19347 11.3261 1.66667 10 1.66667C8.67392 1.66667 7.40215 2.19347 6.46447 3.13113C5.52678 4.06881 5 5.34058 5 6.66667V9.16667L3.33333 10.8333V12.5H16.6667V10.8333L15 9.16667V6.66667Z" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.5 12.5V13.3333C7.5 14.2174 7.85119 15.0653 8.47631 15.6904C9.10143 16.3155 9.94928 16.6667 10.8333 16.6667C11.7174 16.6667 12.5652 16.3155 13.1904 15.6904C13.8155 15.0653 14.1667 14.2174 14.1667 13.3333V12.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full animate-notification-glow">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {isNotifOpen && (
              <div className="absolute right-0 mt-2 w-[380px] bg-white rounded-[20px] shadow-lg border border-[#EBEBEB] overflow-hidden z-50 p-[10px]">
                <div className="p-4 border-b border-[#EBEBEB] flex items-center justify-between">
                  <div>
                    <h2 className="text-[16px] font-semibold text-[#1A1D1F]">Notifications</h2>
                    <p className="text-[12px] text-[#84878B]">Unread: {unreadCount}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAll}
                        className="text-[12px] text-[#40C191] hover:underline font-medium"
                      >
                        Mark all
                      </button>
                    )}
                    <button
                      className="text-[12px] text-[#40C191] hover:underline font-medium"
                      onClick={() => {
                        navigate('/settings/notifications?tab=notifications');
                        setIsNotifOpen(false);
                      }}
                    >
                      View all
                    </button>
                  </div>
                </div>
                <div className="max-h-[480px] overflow-y-auto">
                  {loadingNotif ? (
                    <div className="p-8 text-center text-[#84878B] text-sm">Loading notifications...</div>
                  ) : notifications.length === 0 ? (
                    <div className="p-8 text-center text-[#84878B] text-sm">No notifications</div>
                  ) : (
                    <>
                      {['today', 'yesterday', 'older'].map((section) => {
                        const label =
                          section === 'today' ? 'Today' : section === 'yesterday' ? 'Yesterday' : 'Older';
                        const list = groupedNotifs[section] || [];
                        if (list.length === 0) return null;
                        return (
                          <div
                            key={section}
                            className={`p-4 ${section !== 'today' ? 'border-t border-[#EBEBEB]' : ''}`}
                          >
                            <h3 className="text-[12px] font-medium text-[#84878B] mb-3">{label}</h3>
                            <div className="space-y-2">
                              {list.map((n) => (
                                <button
                                  key={n._id || n.id}
                                  className={`flex items-start gap-3 w-full p-2 rounded-lg hover:bg-[#F8F9FA] transition-colors group text-left ${
                                    !n.read ? 'bg-[#F8F9FA]' : ''
                                  }`}
                                  onClick={() => handleNotificationClick(n)}
                                >
                                  {getIcon(n.type)}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[14px] text-[#1A1D1F] mb-1 group-hover:text-[#40C191] transition-colors line-clamp-1">
                                      {n.title || n.body}
                                    </p>
                                    <p className="text-[12px] text-[#84878B] mb-1 line-clamp-2">{n.body}</p>
                                    <span className="text-[12px] text-[#84878B]">{formatTimeAgo(n.createdAt)}</span>
                                  </div>
                                  {!n.read && (
                                    <div className="w-2 h-2 bg-[#40C191] rounded-full flex-shrink-0 mt-1"></div>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Profile */}
          <div className="relative">
            <button 
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center space-x-2 p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <img 
                src="https://ui-avatars.com/api/?name=John+Doe&background=34D399&color=fff&size=128" 
                alt="User" 
                className="w-10 h-10 rounded-full object-cover ring-2 ring-emerald-400"
              />
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M5 7.5L10 12.5L15 7.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Profile Dropdown */}
            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                {/* <Link 
                  to="/settings/personal-details" 
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Profile Settings
                </Link> */}
                <Link 
                  to="/settings/personal-details" 
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Account Settings
                </Link>
                <hr className="my-2" />
                <button onClick={logout} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;